import { Component, forwardRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TextInputComponent } from './../text-input/text-input.component';

@Component({
  selector: 'mpro-datepicker',
  templateUrl: './datepicker.component.html',
  styleUrls: ['./datepicker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatepickerComponent),
      multi: true
    }
  ]
})
export class DatepickerComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() public title?: string;
  @Input() public subtitle?: string;
  public isOpen: boolean = false;
  public dateInput: UntypedFormControl = new UntypedFormControl(null);
  public date: UntypedFormControl = new UntypedFormControl(null);
  public minDate: NgbDateStruct;
  @ViewChild(TextInputComponent) public textInput: TextInputComponent;

  private onChange = (_: any) => { };
  private onTouched = (_: any) => { };
  private ngUnsubscribe: Subject<void> = new Subject();

  constructor() { }

  public writeValue(val: string): void {
    if (val) {
      this.date.setValue(this.parseDate(val));
    }
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn
  }

  public setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.dateInput.disable();
    }
  }

  public ngOnInit(): void {
    const now = new Date();
    this.minDate = {
      day: now.getDate(),
      month: now.getMonth() + 1,
      year: now.getFullYear() - 100
    };

    this.date.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((value: NgbDateStruct | null) => {
        if (value !== null) {
          this.dateInput.setValue(this.formatDateDisplayValue(value));
          this.isOpen = false;
        }

        this.onChange(value ? this.formatDate(value) : null);
      });
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public clearDate() {
    this.date.reset();
    this.dateInput.reset();
    setTimeout(() => {
      this.textInput.focused = false;
      this.textInput.input.nativeElement.blur();
      this.isOpen = false;
    });
  }

  public closeWhenClickedOutside(event: MouseEvent) {
    this.isOpen = false;
  }

  private parseDate(date: string): NgbDateStruct | null {
    // date format is assumed to be yyyy-mm-dd
    if (!date) {
      return null;
    }

    const split = date.split('-');
    if (split.length !== 3) {
      return null;
    }

    return { day: +split[2], month: +split[1], year: +split[0] }
  }

  private formatDate(date: NgbDateStruct | null): string {
    if (!date) {
      return '';
    }

    return `${date.year}-${this.prependZeroIfNeeded(date.month)}-${this.prependZeroIfNeeded(date.day)}`;
  }

  private formatDateDisplayValue(date: NgbDateStruct | null): string {
    if (!date) {
      return '';
    }

    return `${this.prependZeroIfNeeded(date.day)}/${this.prependZeroIfNeeded(date.month)}/${date.year}`;
  }

  private prependZeroIfNeeded(dateNum: number): string {
    if (dateNum < 10) {
      return `0${dateNum}`;
    }

    return `${dateNum}`;
  }
}
